import type { FlashList } from '@shopify/flash-list';
import type { Event } from '@sunrise-ui/api/events';
import { Colors } from '@sunrise-ui/primitives';
import { DateTime } from 'luxon';
import type { RefObject } from 'react';
import type { SectionList as RNSectionList } from 'react-native';
import type { MarkedDates } from 'react-native-calendars/src/types';

export type EventSection = {
  title: string;
  data: Event[][];
};

export type FlashListItem =
  | { type: 'header'; startDateTime: string; sectionIndex: number }
  | {
      type: 'section-list';
      events: Event[];
      sectionIndex: number;
    };

export const groupEventsByDate = (
  events: Event[] | undefined,
  isMobile: boolean,
  sortDirection: 'asc' | 'desc' = 'asc',
): EventSection[] => {
  if (!events) return [];

  const validEvents = events.filter((event) => event.start_date);

  const groups: Record<string, Event[]> = {};
  const now = DateTime.now();
  const todayKey = now.toISODate() ?? '';

  validEvents.forEach((event) => {
    const startDate = DateTime.fromISO(event.start_date!);
    const endDate = event.scheduled_end_date
      ? DateTime.fromISO(event.scheduled_end_date)
      : null;

    // For ongoing multi-day events (started in past but not ended), group under today
    const isOngoing =
      startDate < now.startOf('day') &&
      endDate !== null &&
      endDate >= now.startOf('day');

    const dateKey = isOngoing ? todayKey : (startDate.toISODate() ?? '');

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(event);
  });

  return Object.entries(groups)
    .map(([dateKey, data]) => {
      const processedData = data.reduce<Event[][]>((acc, event, index) => {
        if (isMobile) acc.push([event]);
        else if (index % 2 === 0) {
          // Start a new row
          const nextEvent = data[index + 1];
          if (nextEvent) acc.push([event, nextEvent]);
          else acc.push([event]);
        }
        return acc;
      }, []);

      // Use the first event's full ISO timestamp as title for CardListDateHeader to parse
      const firstEventTimestamp = data[0]?.start_date ?? dateKey;
      return { title: firstEventTimestamp, data: processedData };
    })
    .sort((a, b) => {
      const dateA = DateTime.fromISO(a.data[0][0].start_date!);
      const dateB = DateTime.fromISO(b.data[0][0].start_date!);
      const comparison = dateA.toMillis() - dateB.toMillis();
      return sortDirection === 'desc' ? -comparison : comparison;
    });
};

export const findNearestSectionIndex = (
  selectedDate: string,
  groupedEvents: EventSection[],
  sortDirection: 'asc' | 'desc' = 'asc',
): number => {
  const isAsc = sortDirection === 'asc';
  const selectedDateTime = DateTime.fromISO(selectedDate);
  let nearestSectionIndex = -1;

  for (const [index, section] of groupedEvents.entries()) {
    const sectionDate = DateTime.fromISO(section.data[0][0].start_date!);
    const shiftedDate = isAsc
      ? sectionDate.endOf('day')
      : sectionDate.startOf('day');
    if (
      isAsc ? shiftedDate < selectedDateTime : shiftedDate > selectedDateTime
    ) {
      nearestSectionIndex = index;
    } else {
      nearestSectionIndex += 1;
      break;
    }
  }

  return nearestSectionIndex;
};

type ScrollToDateParams = {
  selectedDate: string;
  sectionListRef: RefObject<RNSectionList<Event[], EventSection>>;
  groupedEvents: EventSection[];
  sortDirection?: 'asc' | 'desc';
};

export const scrollToDate = ({
  selectedDate,
  sectionListRef,
  groupedEvents,
  sortDirection = 'asc',
}: ScrollToDateParams) => {
  if (!sectionListRef.current) return;

  const nearestSectionIndex = findNearestSectionIndex(
    selectedDate,
    groupedEvents,
    sortDirection,
  );

  // Scroll to the section by index using scrollToLocation
  // This works with dynamic heights since it doesn't rely on offset calculations
  if (nearestSectionIndex >= 0 && nearestSectionIndex < groupedEvents.length) {
    sectionListRef.current.scrollToLocation({
      sectionIndex: nearestSectionIndex,
      itemIndex: 0,
      animated: true,
      viewPosition: 0,
    });
  }
};

export const createMarkedDates = (events: Event[] | undefined): MarkedDates => {
  if (!events) return {};

  const marked: MarkedDates = {};

  for (const event of events) {
    if (!event.start_date) continue;
    const eventDate = DateTime.fromISO(event.start_date);
    const dateKey = eventDate.toISODate();
    if (!dateKey) continue;

    if (!marked[dateKey]) {
      marked[dateKey] = {
        marked: true,
        dotColor: Colors.pink0,
        dots: [{ color: Colors.pink0 }],
      };
    } else if (marked[dateKey].dots!.length < 4) {
      marked[dateKey] = {
        ...marked[dateKey],
        dots: [...marked[dateKey].dots!, { color: Colors.pink0 }],
      };
    }
  }

  return marked;
};

// Group events by date for FlashList (simpler structure)
export const groupEventsByDateForFlashList = (
  events: Event[] | undefined,
  sortDirection: 'asc' | 'desc' = 'asc',
): { startDateTime: string; events: Event[] }[] => {
  if (!events) return [];

  const validEvents = events.filter((event) => event.start_date);

  const groups: Record<string, Event[]> = {};
  const now = DateTime.now();
  const todayKey = now.toISODate() ?? '';

  validEvents.forEach((event) => {
    const startDate = DateTime.fromISO(event.start_date!);
    const endDate = event.scheduled_end_date
      ? DateTime.fromISO(event.scheduled_end_date)
      : null;

    // For ongoing multi-day events (started in past but not ended), group under today
    const isOngoing =
      startDate < now.startOf('day') &&
      endDate !== null &&
      endDate >= now.startOf('day');

    const dateKey = isOngoing ? todayKey : (startDate.toISODate() ?? '');

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(event);
  });

  return Object.entries(groups)
    .map(([dateKey, eventsInGroup]) => ({
      // Use today's date for the header if this is the today group
      startDateTime:
        dateKey === todayKey
          ? now.toISO()!
          : (eventsInGroup[0]?.start_date ?? dateKey),
      events: eventsInGroup,
    }))
    .sort((a, b) => {
      const dateA = DateTime.fromISO(a.startDateTime);
      const dateB = DateTime.fromISO(b.startDateTime);
      const comparison = dateA.toMillis() - dateB.toMillis();
      return sortDirection === 'desc' ? -comparison : comparison;
    });
};

// FlashList-specific data creation
export const createFlashListData = (
  groupedEvents: { startDateTime: string; events: Event[] }[],
): FlashListItem[] => {
  const items: FlashListItem[] = [];

  groupedEvents.forEach(({ startDateTime, events }, sectionIndex) => {
    // Add section header
    items.push({
      type: 'header',
      startDateTime,
      sectionIndex,
    });

    // Add row with all events from this section
    items.push({
      type: 'section-list',
      events,
      sectionIndex,
    });
  });

  return items;
};

// Find nearest section index for FlashList scrolling
export const findNearestFlashListSectionIndex = (
  selectedDate: string,
  groupedEvents: { startDateTime: string; events: Event[] }[],
  sortDirection: 'asc' | 'desc' = 'asc',
): number => {
  const isAsc = sortDirection === 'asc';
  const selectedDateTime = DateTime.fromISO(selectedDate);
  let nearestSectionIndex = -1;

  for (const [index, section] of groupedEvents.entries()) {
    const sectionDate = DateTime.fromISO(section.events[0].start_date!);
    const shiftedDate = isAsc
      ? sectionDate.endOf('day')
      : sectionDate.startOf('day');

    if (
      isAsc ? shiftedDate < selectedDateTime : shiftedDate > selectedDateTime
    ) {
      nearestSectionIndex = index;
    } else {
      nearestSectionIndex = Math.max(0, nearestSectionIndex + 1);
      break;
    }
  }

  return nearestSectionIndex;
};

type ScrollToFlashListDateParams = {
  selectedDate: string;
  flashListRef: RefObject<FlashList<FlashListItem>>;
  groupedEvents: { startDateTime: string; events: Event[] }[];
  viewableItemIndicesRef: RefObject<number[]>;
};

// Scroll to date in FlashList
export const scrollToFlashListDate = ({
  selectedDate,
  flashListRef,
  groupedEvents,
  viewableItemIndicesRef,
}: ScrollToFlashListDateParams) => {
  if (!flashListRef.current) return;

  const nearestSectionIndex = findNearestFlashListSectionIndex(
    selectedDate,
    groupedEvents,
  );

  // Each section = 1 header + 1 section
  let targetIndex = -1;
  if (nearestSectionIndex >= 0 && nearestSectionIndex < groupedEvents.length) {
    targetIndex = nearestSectionIndex * 2;
  }

  if (targetIndex >= 0) {
    flashListRef.current?.scrollToIndex({
      index: targetIndex,
      animated: true,
      viewPosition: 0.1,
    });
    setTimeout(() => {
      const viewableItemIndex = viewableItemIndicesRef?.current ?? [
        targetIndex,
      ];
      if (!viewableItemIndex.includes(targetIndex)) {
        scrollToFlashListDate({
          selectedDate,
          flashListRef,
          groupedEvents,
          viewableItemIndicesRef,
        });
      }
    }, 500);
  }
};
